import {
  ChatBubbleLeftRightIcon,
  QueueListIcon,
} from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import ChatNavigation from "../components/chat/chat-navigation";
import Card from "../components/shared/card";
import { SubmitButton } from "../components/shared/buttons/submit-button";
import ThemedSpinner from "../components/shared/spinner";
import Typography from "../components/shared/typography";
import { useLoadAdminChats } from "../hooks/useLoadAdminChats";
import { useUserDetails } from "../hooks/useUserDetails";

const AdminAllChatsPage = () => {
  const { userDetails, isLoading: isUserLoading } = useUserDetails();
  const isAdmin = userDetails?.type === "admin";
  const navigate = useNavigate();
  const {
    adminUsers,
    isLoading: isLoadingChats,
    isError: isChatsError,
  } = useLoadAdminChats(Boolean(isAdmin));

  const totalChats = adminUsers.length;

  if (!isAdmin && !isUserLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto text-center space-y-4">
          <Typography variant="h3" className="text-terracotta">
            Admin access required
          </Typography>
          <Typography variant="body1" className="text-brown-600">
            You need an administrator account to view all chats.
          </Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <ChatNavigation />

        <div className="flex items-center gap-3">
          <QueueListIcon className="w-8 h-8 text-terracotta" />
          <div>
            <Typography variant="h2" className="text-terracotta">
              All Chats
            </Typography>
            <Typography variant="body2" className="text-brown-600">
              Monitor every chat thread across users in one place.
            </Typography>
          </div>
        </div>

        <Card variant="cream" elevation={2}>
          <Card.Body className="grid gap-4 sm:grid-cols-3">
            <div>
              <Typography variant="caption" className="uppercase text-brown-500">
                Total Chats
              </Typography>
              <Typography variant="h4" className="text-terracotta">
                {totalChats}
              </Typography>
            </div>
            <div>
              <Typography variant="caption" className="uppercase text-brown-500">
                Admin Only
              </Typography>
              <Typography variant="h4" className="text-brown-800">
                Yes
              </Typography>
            </div>
            <div>
              <Typography variant="caption" className="uppercase text-brown-500">
                Last Refresh
              </Typography>
              <Typography variant="h5" className="text-brown-800">
                Live
              </Typography>
            </div>
          </Card.Body>
        </Card>

        {isLoadingChats ? (
          <div className="flex justify-center py-12">
            <ThemedSpinner size="lg" />
          </div>
        ) : isChatsError ? (
          <div className="rounded-2xl border border-red-100 bg-red-50 p-6 text-center">
            <Typography variant="h4" className="text-red-600 mb-2">
              Couldn’t load chats
            </Typography>
            <Typography variant="body2" className="text-red-700">
              Please refresh the page to try again.
            </Typography>
          </div>
        ) : adminUsers.length === 0 ? (
          <Card variant="cream">
            <Card.Body className="text-center space-y-2">
              <Typography variant="h4" className="text-brown-700">
                No chats yet
              </Typography>
              <Typography variant="body2" className="text-brown-500">
                When users start messaging, their conversations will appear here.
              </Typography>
            </Card.Body>
          </Card>
        ) : (
          <div className="grid gap-4">
            {adminUsers.map((user) => {
              const initials = `${user.first_name?.[0] ?? ""}${
                user.last_name?.[0] ?? ""
              }`.trim() || "?";
              const displayName = `${user.first_name ?? ""} ${
                user.last_name ?? ""
              }`.trim() || "Unnamed user";

              return (
                <Card key={user.id} variant="cream" elevation={1}>
                  <Card.Body className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-terracotta-100 flex items-center justify-center text-sm font-semibold text-terracotta-700">
                        {initials}
                      </div>
                      <div>
                        <Typography variant="h5" className="text-primary">
                          {displayName}
                        </Typography>
                        <Typography variant="body2" className="text-gray-600">
                          Chat ID: {user.id}
                        </Typography>
                      </div>
                    </div>
                    <SubmitButton
                      size="sm"
                      color="terracotta"
                      onClick={() =>
                        navigate(`/admin/all-chats/${user.id}`, {
                          state: { user },
                        })
                      }
                      icon={<ChatBubbleLeftRightIcon className="w-4 h-4" />}
                    >
                      View chat
                    </SubmitButton>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAllChatsPage;
